import React, { memo, useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { IconDownload, IconFile, IconPhoto, IconX, IconZoomIn, IconZoomOut } from "@tabler/icons-react";
import "./MessageMedia.css";

/**
 * Lightbox para visualização de imagem em tela cheia
 */
const ImageLightbox = memo(({ src, alt, onClose }) => {
  const [scale, setScale] = useState(1);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") onClose();
    if (e.key === "+" || e.key === "=") setScale((s) => Math.min(s + 0.25, 4));
    if (e.key === "-") setScale((s) => Math.max(s - 0.25, 0.5));
    if (e.key === "0") setScale(1);
  }, [onClose]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [handleKeyDown]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div className="sc-lightbox" onClick={handleBackdropClick}>
      <div className="sc-lightbox__toolbar">
        <button
          className="sc-lightbox__btn"
          onClick={() => setScale((s) => Math.max(s - 0.25, 0.5))}
          title="Diminuir (−)"
        >
          <IconZoomOut size={20} />
        </button>
        <span className="sc-lightbox__scale">{Math.round(scale * 100)}%</span>
        <button
          className="sc-lightbox__btn"
          onClick={() => setScale((s) => Math.min(s + 0.25, 4))}
          title="Aumentar (+)"
        >
          <IconZoomIn size={20} />
        </button>
        <button
          className="sc-lightbox__btn"
          onClick={() => {
            fetch(src)
              .then((res) => res.blob())
              .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "imagem." + (blob.type.split("/")[1] || "jpg");
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              })
              .catch(() => window.open(src, "_blank"));
          }}
          title="Baixar"
        >
          <IconDownload size={20} />
        </button>
        <button className="sc-lightbox__btn sc-lightbox__btn--close" onClick={onClose} title="Fechar (Esc)">
          <IconX size={22} />
        </button>
      </div>
      <div className="sc-lightbox__content">
        <img
          src={src}
          alt={alt}
          className="sc-lightbox__image"
          style={{ transform: `scale(${scale})` }}
          draggable={false}
        />
      </div>
    </div>
  );
});
ImageLightbox.displayName = "ImageLightbox";


/**
 * Renderiza mídia de mensagem (imagem, áudio, vídeo, documento)
 */
const MessageMedia = memo(({ message, isFromMe }) => {
  const [imageError, setImageError] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);


  const mediaUrl = message.mediaUrl || message.media_url || message.audioUrl;
  const mimeType = message.mimetype || message.mimeType || "";

  // media_type tem prioridade pois message.type pode ser "chat" (genérico da API)
  const rawType = message.type || "";
  const mediaType = message.media_type || (rawType !== "chat" && rawType !== "text" ? rawType : "") || "text";

  // Extrair nome do arquivo da URL se não vier na mensagem
  const getFileNameFromUrl = (url) => {
    if (!url) return "arquivo";
    try {
      const path = new URL(url).pathname;
      const name = path.split("/").pop();
      return name || "arquivo";
    } catch {
      return "arquivo";
    }
  };
  const fileName = message.fileName || message.filename || getFileNameFromUrl(mediaUrl);

  // Imagem
  if (mediaType === "image" || mimeType.startsWith("image/")) {
    return (
      <div className="sc-media sc-media--image">
        {!imageError ? (
          <>
            <img
              src={mediaUrl}
              alt="Imagem"
              className="sc-media__image"
              onError={() => setImageError(true)}
              onClick={() => setLightboxOpen(true)}
              loading="lazy"
            />
            {lightboxOpen && ReactDOM.createPortal(
              <ImageLightbox
                src={mediaUrl}
                alt="Imagem"
                onClose={() => setLightboxOpen(false)}
              />,
              document.body
            )}
          </>
        ) : (
          <div className="sc-media__placeholder">
            <IconPhoto size={32} />
            <span>Imagem indisponível</span>
          </div>
        )}
      </div>
    );
  }

  // Áudio
  if (mediaType === "audio" || mediaType === "ptt" || mimeType.startsWith("audio/")) {
    return (
      <div className={`sc-media sc-media--audio ${isFromMe ? "sc-media--audio-sent" : ""}`}>
        <audio
          src={mediaUrl}
          controls
          className="sc-media__audio"
        />
        {mediaType === "ptt" && (
          <span className="sc-media__ptt-badge">Mensagem de voz</span>
        )}
      </div>
    );
  }

  // Vídeo
  if (mediaType === "video" || mimeType.startsWith("video/")) {
    return (
      <div className="sc-media sc-media--video">
        <video
          src={mediaUrl}
          controls
          className="sc-media__video"
          preload="metadata"
        />
      </div>
    );
  }

  // Documento (inclui PDFs detectados pela URL mesmo sem mediaType correto)
  const urlIsPdf = mediaUrl && mediaUrl.toLowerCase().endsWith(".pdf");
  if (mediaType === "document" || mediaType === "file" || urlIsPdf) {
    const isPdf = mimeType.includes("pdf") || fileName.toLowerCase().endsWith(".pdf");

    const getFileIcon = () => {
      if (isPdf) return "📄";
      if (mimeType.includes("word") || mimeType.includes("document")) return "📝";
      if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "📊";
      if (mimeType.includes("presentation") || mimeType.includes("powerpoint")) return "📽️";
      if (mimeType.includes("zip") || mimeType.includes("rar") || mimeType.includes("archive")) return "📦";
      return "📎";
    };

    return (
      <div className="sc-media sc-media--document">
        <div className="sc-media__document-icon">{getFileIcon()}</div>
        <div className="sc-media__document-info">
          <span className="sc-media__document-name">{fileName}</span>
          {mimeType && (
            <span className="sc-media__document-type">
              {mimeType.split("/").pop()?.toUpperCase()}
            </span>
          )}
        </div>
        <a
          href={mediaUrl}
          download={fileName}
          target="_blank"
          rel="noopener noreferrer"
          className="sc-media__download-btn"
          title="Baixar arquivo"
        >
          <IconDownload size={18} />
        </a>
      </div>
    );
  }

  // Sticker
  if (mediaType === "sticker") {
    return (
      <div className="sc-media sc-media--sticker">
        <img
          src={mediaUrl}
          alt="Sticker"
          className="sc-media__sticker"
          loading="lazy"
        />
      </div>
    );
  }

  // Location
  if (mediaType === "location") {
    const lat = message.latitude || message.lat;
    const lng = message.longitude || message.lng;
    const mapUrl = `https://www.google.com/maps?q=${lat},${lng}`;
    const staticMapUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=300x200&markers=color:red%7C${lat},${lng}`;

    return (
      <div className="sc-media sc-media--location">
        <a href={mapUrl} target="_blank" rel="noopener noreferrer">
          <div className="sc-media__location-preview">
            <span className="sc-media__location-icon">📍</span>
            <span className="sc-media__location-text">Ver localização</span>
          </div>
        </a>
        {message.address && (
          <p className="sc-media__caption">{message.address}</p>
        )}
      </div>
    );
  }

  // Contact (vCard)
  if (mediaType === "vcard" || mediaType === "contact") {
    return (
      <div className="sc-media sc-media--contact">
        <div className="sc-media__contact-icon">👤</div>
        <div className="sc-media__contact-info">
          <span className="sc-media__contact-name">
            {message.contactName || message.displayName || "Contato"}
          </span>
          {message.contactNumber && (
            <span className="sc-media__contact-number">{message.contactNumber}</span>
          )}
        </div>
      </div>
    );
  }

  // Fallback para tipos desconhecidos com mídia
  if (mediaUrl) {
    return (
      <div className="sc-media sc-media--unknown">
        <IconFile size={24} />
        <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
          Ver anexo
        </a>
      </div>
    );
  }

  return null;
});

MessageMedia.displayName = "MessageMedia";

export default MessageMedia;
